/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { Drawer } from 'app/components/organisms/drawer/drawer';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useProjects } from 'app/hooks/requests/projects/use-projects';
import type { Transaction } from 'app/types/transaction';
import { FolderKanban } from 'lucide-react';
import { Checkbox } from 'app/components/ui/checkbox';
import { Input } from 'app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { Slider } from 'app/components/ui/slider';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * `Props` type.
 */

type Props = {
  item: Transaction;
  refetch: () => void;
};

/**
 * `Budget` type.
 */

type Budget = {
  id: number;
  name: string;
};

/**
 * `PaymentType` type.
 */

type PaymentType = {
  id: number;
  name: string;
};

/**
 * Default tax percentage.
 */

const defaultTax = 23;

/**
 * Export `LinkProjectAction` component.
 */

export function LinkProjectAction({ item, refetch }: Props): JSX.Element {
  const client = useRequestProvider();
  const { data: projects } = useProjects();
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [existingMovementId, setExistingMovementId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [budgetId, setBudgetId] = useState<number | null>(null);
  const [paymentTypeId, setPaymentTypeId] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState<moment.Moment>(moment());
  const [paymentDate, setPaymentDate] = useState<moment.Moment>(moment());
  const [hasTax, setHasTax] = useState(false);
  const [tax, setTax] = useState(defaultTax);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);

  const absAmount = useMemo(() => new BigNumber(item.amount).abs(), [item.amount]);

  const { amount, total } = useMemo(() => {
    if (!hasTax) {
      return { amount: absAmount.toString(), total: absAmount.toString() };
    }

    const taxMultiplier = new BigNumber(tax).dividedBy(100).plus(1);
    const computedAmount = absAmount.dividedBy(taxMultiplier);

    return {
      amount: computedAmount.decimalPlaces(2).toString(),
      total: absAmount.toString()
    };
  }, [absAmount, hasTax, tax]);

  const handleOpen = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation();

      // Pre-populate from the project item this transaction is already linked to.
      const existing = (item.projectMovements ?? [])[0] as any;

      if (existing) {
        setExistingMovementId(existing.id);
        setProjectId(existing.projectId ?? existing.project?.id ?? null);
        setBudgetId(existing.budgetId ?? existing.budget?.id ?? null);
        setPaymentTypeId(existing.paymentTypeId ?? existing.paymentType?.id ?? null);
        setDescription(existing.description ?? '');
        setDate(moment(existing.date ?? item.date));
        setPaymentDate(moment(existing.paymentDate ?? existing.date ?? item.date));
        setHasTax(!!existing.hasTax);
        setTax(existing.tax ?? defaultTax);
      } else {
        setExistingMovementId(null);
        setProjectId(null);
        setBudgetId(null);
        setPaymentTypeId(null);
        setDescription(item.description || item.bank_description || '');
        setDate(moment(item.date));
        setPaymentDate(moment(item.date));
        setHasTax(false);
        setTax(defaultTax);
      }

      setOpen(true);
    },
    [item.bank_description, item.date, item.description, item.projectMovements]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setExistingMovementId(null);
    setProjectId(null);
    setBudgetId(null);
    setPaymentTypeId(null);
    setDescription('');
    setBudgets([]);
    setPaymentTypes([]);
  }, []);

  useEffect(() => {
    if (!projectId) {
      setBudgets([]);
      setPaymentTypes([]);

      return;
    }

    const selectedProject = projects?.find(p => p.id === projectId);

    if (!existingMovementId && selectedProject?.defaultIVA) {
      setTax(selectedProject.defaultIVA);
    }

    async function fetchProjectData() {
      try {
        const [budgetsResponse, paymentTypesResponse] = await Promise.all([
          client.get(
            resolveAppUrl(apiEndpoints.projectBudgets, {
              interpolations: { projectId }
            })
          ),
          client.get(
            resolveAppUrl(apiEndpoints.projectPaymentTypes, {
              interpolations: { projectId }
            })
          )
        ]);

        setBudgets(budgetsResponse.data?.data ?? []);
        setPaymentTypes(paymentTypesResponse.data?.data ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    fetchProjectData();
  }, [client, projectId, projects, existingMovementId]);

  const handleProjectChange = useCallback((value: number) => {
    setProjectId(value);
    setBudgetId(null);
    setPaymentTypeId(null);
  }, []);

  const handleApply = useCallback(async () => {
    if (!projectId || !budgetId || !paymentTypeId) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        date: date.format('YYYY-MM-DD HH:mm:ss'),
        description,
        budgetId,
        amount,
        hasTax,
        tax: hasTax ? tax : 0,
        total,
        paid: true,
        paymentDate: paymentDate.format('YYYY-MM-DD HH:mm:ss'),
        paymentTypeId,
        transactionId: parseInt(item.id, 10)
      };

      // Update the existing project item when re-linking, otherwise create it.
      if (existingMovementId) {
        await client.patch(
          resolveAppUrl(apiEndpoints.projectMovementById, {
            interpolations: { projectId, id: existingMovementId }
          }),
          payload
        );
      } else {
        await client.post(resolveAppUrl(apiEndpoints.projectMovements, { interpolations: { projectId } }), payload);
      }

      refetch();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    amount,
    budgetId,
    client,
    date,
    description,
    existingMovementId,
    handleClose,
    hasTax,
    item.id,
    paymentDate,
    paymentTypeId,
    projectId,
    refetch,
    tax,
    total
  ]);

  const handleDelete = useCallback(async () => {
    if (!existingMovementId || !projectId) {
      return;
    }

    try {
      setDeleting(true);

      await client.delete(
        resolveAppUrl(apiEndpoints.projectMovementById, {
          interpolations: { projectId, id: existingMovementId }
        })
      );

      refetch();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  }, [client, existingMovementId, handleClose, projectId, refetch]);

  return (
    <>
      <Button icon={<FolderKanban className={'h-4 w-4'} />} onClick={handleOpen} size={'small'} />

      <Drawer
        buttons={[
          ...(existingMovementId
            ? [
                <Button danger key={'delete'} loading={isDeleting} onClick={handleDelete}>
                  {'Remover'}
                </Button>
              ]
            : []),
          <Button
            disabled={!projectId || !budgetId || !paymentTypeId}
            key={'apply'}
            loading={isLoading}
            onClick={handleApply}
            type={'primary'}
          >
            {existingMovementId ? 'Guardar' : 'Aplicar'}
          </Button>
        ]}
        isLoading={isLoading || isDeleting}
        isOpen={isOpen}
        maskClosable={!isLoading && !isDeleting}
        onClose={handleClose}
        title={existingMovementId ? 'Editar associação' : 'Associar a projeto'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Projeto'}</div>

            <Select
              onValueChange={value => handleProjectChange(Number(value))}
              value={projectId != null ? String(projectId) : undefined}
            >
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Selecionar projeto'} />
              </SelectTrigger>

              <SelectContent>
                {projects?.map(p => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Budget'}</div>

            <Select
              disabled={!projectId}
              onValueChange={value => setBudgetId(Number(value))}
              value={budgetId != null ? String(budgetId) : undefined}
            >
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Selecionar budget'} />
              </SelectTrigger>

              <SelectContent>
                {budgets.map(b => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Tipo de Pagamento'}</div>

            <Select
              disabled={!projectId}
              onValueChange={value => setPaymentTypeId(Number(value))}
              value={paymentTypeId != null ? String(paymentTypeId) : undefined}
            >
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Selecionar tipo de pagamento'} />
              </SelectTrigger>

              <SelectContent>
                {paymentTypes.map(pt => (
                  <SelectItem key={pt.id} value={String(pt.id)}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Descrição'}</div>

            <Input
              onChange={e => setDescription(e.target.value)}
              placeholder={'Descrição do movimento'}
              value={description}
            />
          </div>

          <div className={'flex gap-4'}>
            <div className={'flex-1'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Data de Execução'}</div>

              <Input
                onChange={event => event.target.value && setDate(moment(event.target.value, 'YYYY-MM-DD'))}
                type={'date'}
                value={date ? date.format('YYYY-MM-DD') : ''}
              />
            </div>

            <div className={'flex-1'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Data de Pagamento'}</div>

              <Input
                onChange={event => event.target.value && setPaymentDate(moment(event.target.value, 'YYYY-MM-DD'))}
                type={'date'}
                value={paymentDate ? paymentDate.format('YYYY-MM-DD') : ''}
              />
            </div>
          </div>

          <div>
            <label className={'flex items-center gap-2 text-sm text-foreground'}>
              <Checkbox checked={hasTax} onCheckedChange={checked => setHasTax(checked === true)} />

              {'Com IVA'}
            </label>
          </div>

          {hasTax && (
            <div>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'IVA %'}</div>

              <div className={'flex items-center gap-4'}>
                <div className={'flex-[4]'}>
                  <Slider max={100} min={0} onValueChange={values => setTax(values[0])} value={[tax]} />
                </div>

                <div className={'flex-[2]'}>
                  <Input
                    max={100}
                    min={0}
                    onChange={event => {
                      const next = event.target.value === '' ? defaultTax : Number(event.target.value);

                      setTax(Number.isNaN(next) ? defaultTax : next);
                    }}
                    type={'number'}
                    value={tax}
                  />
                </div>
              </div>

              <div className={'mt-2 text-xs text-muted-foreground'}>
                {'Valor s/ IVA: '}

                {amount}

                {' · Total: '}

                {total}
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
}
